import { AfterViewInit, Component, effect, input, OnDestroy, ViewChild, } from '@angular/core';
import { BusinessCategory, Lead } from '../../lead.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { calculateDistanceInMeters, GeolocationService, } from '../../services/geolocation/geolocation.service';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import { MatPaginator } from '@angular/material/paginator';
import { Subscription, Unsubscribable } from 'rxjs';
import { LeadService } from '../../services/leads/lead.service';
import { provideTranslateablePaginator } from '../../reusable-components/translateable-paginator';
import { LeadFilterService } from '../lead-filter.service';
import { AllLeadsFilterComponent } from './all-leads-filter/all-leads-filter.component';
import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { MatCard } from '@angular/material/card';

@Component({
  selector: 'app-all-leads-list',
  imports: [
    MatTableModule,
    MatSortModule,
    MatCheckbox,
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    MatPaginator,
    AllLeadsFilterComponent,
    MatCard,
  ],
  providers: [provideTranslateablePaginator()],
  templateUrl: './all-leads-list.component.html',
  styleUrl: './all-leads-list.component.scss',
})
export class AllLeadsListComponent implements OnDestroy, AfterViewInit {
  leadServiceSubscription: Subscription | undefined;

  columnsToDisplay = ['name', 'postalCode', 'city', 'select'];
  tableDataSource: MatTableDataSource<Lead> = new MatTableDataSource<Lead>([]);

  mediaQueryList: Unsubscribable;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    protected leadService: LeadService,
    private readonly geolocationService: GeolocationService,
    private readonly mediaQueryService: MediaQueryService,
    protected filterService: LeadFilterService,
    private readonly snackbarService: SnackbarService,
  ) {
    this.mediaQueryList = this.adaptiveTableLayout(this.mediaQueryService);

    effect(() => {
      this.filterService.postalCodePrefix(); // Trigger effect when filters change
      this.filterService.priority();
      this.filterService.daysToDueDate();
      this.filterService.businessCategory();
      this.filterService.leadType();
      this.applyFilters(this.filterService.filterPredicate());
    });
  }

  ngAfterViewInit() {
    this.initializeMatTableAndMatSelect();
  }

  private applyFilters(filterPredicate: (lead: Lead) => boolean) {
    this.tableDataSource.filterPredicate = filterPredicate;
    this.tableDataSource.filter = 'doFilter'; // Trigger actual filtering
    this.updateFilterChoices();
    this.defaultSort();
  }

  protected defaultSort() {
    this.tableDataSource.sortData = (data: Lead[]) => {
      return data.slice();
    };
    this.tableDataSource.sort?.sortChange?.emit();
  }

  protected sortByDistance() {
    this.geolocationService.getPosition().then(
      (position) => {
        this.tableDataSource.sortData = (data: Lead[]) => {
          return data.slice().sort((a, b) => {
            return (
              calculateDistanceInMeters(a.coordinates, position.coords) -
              calculateDistanceInMeters(b.coordinates, position.coords)
            );
          });
        };
        this.tableDataSource.sort?.sortChange?.emit();
      },
      () => {
        this.snackbarService.show('components.all-leads-list.location-error');
      },
    );
  }

  toggleLead(lead: Lead) {
    if (this.leadService.isSelected(lead.id)) {
      this.leadService.removeSelectedLead(lead);
    } else {
      this.leadService.selectLead(lead);
    }
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.getCurrentlyVisibleLeads().forEach((lead) => {
        this.leadService.removeSelectedLead(lead);
      });
    } else {
      this.getCurrentlyVisibleLeads().forEach((lead) => {
        this.leadService.selectLead(lead);
      });
    }
  }

  isAllSelected() {
    return this.getCurrentlyVisibleLeads().every((lead) =>
      this.leadService.isSelected(lead.id),
    );
  }

  isAnySelected() {
    return this.leadService.getSelectedLeads().length > 0;
  }

  private getCurrentlyVisibleLeads(): Lead[] {
    if (
      this.tableDataSource.filteredData.length === 0 ||
      this.paginator === undefined
    ) {
      // If no leads are currently visible or paginator is not (yet) initialized, return an empty array
      return [];
    }

    return this.tableDataSource.filteredData.slice(
      this.paginator.pageIndex * this.paginator.pageSize,
      (this.paginator.pageIndex + 1) * this.paginator.pageSize,
    );
  }

  initializeMatTableAndMatSelect() {
    this.leadServiceSubscription = this.leadService.allLeadsChanges$.subscribe(
      () => {
        const leads = this.leadService.getLeads();
        this.tableDataSource = new MatTableDataSource<Lead>(leads);
        this.tableDataSource.sort = new MatSort();
        this.tableDataSource.paginator = this.paginator;
        this.applyFilters(this.filterService.filterPredicate());
      },
    );
  }

  private updateFilterChoices() {
    this.updateCities();
    this.updateStreets();
    this.updateDistricts();
    this.updateBusinessCategories();
    this.updateLeadTypes();
  }

  private updateCities() {
    if (this.filterService.noCitySelected()) {
      const citiesSet = new Set(
        this.tableDataSource.filteredData.map((lead) => lead.address.city),
      );
      this.filterService.cityOptions.set(
        Array.from(citiesSet).sort((a: string, b: string) =>
          a.localeCompare(b),
        ),
      );
    } else if (this.filterService.isPostalCodePrefixEmpty()) {
      const citiesSet = new Set(
        this.tableDataSource.data.map((lead) => lead.address.city),
      );
      this.filterService.cityOptions.set(
        Array.from(citiesSet).sort((a: string, b: string) =>
          a.localeCompare(b),
        ),
      );
    } // If a city and something else is selected, we do not update the cities list
  }

  private updateStreets() {
    if (this.filterService.noStreetSelected()) {
      const streetsSet = new Set(
        this.tableDataSource.filteredData.map((lead) => lead.address.street),
      );
      this.filterService.streetOptions.set(
        Array.from(streetsSet).sort((a: string, b: string) =>
          a.localeCompare(b),
        ),
      );
    }
  }

  private updateDistricts() {
    if (this.filterService.noDistrictSelected()) {
      const districtSet = new Set(
        this.tableDataSource.filteredData.map((lead) => lead.address.district),
      );
      this.filterService.districtOptions.set(
        Array.from(districtSet).sort((a: string, b: string) =>
          a.localeCompare(b),
        ),
      );
    }
  }

  private updateBusinessCategories() {
    if (this.filterService.businessCategory() == undefined) {
      const categories = new Set(
        this.tableDataSource.filteredData.map((lead) => lead.businessCategory),
      );
      this.filterService.businessCategoryOptions.set(
        Array.from(categories).sort((a: BusinessCategory, b: BusinessCategory) =>
          a.localeCompare(b),
        ),
      );
    }
  }

  private updateLeadTypes() {
    if (this.filterService.noLeadTypeSelected()) {
      const leadTypes = new Set(
        this.tableDataSource.filteredData
          .map((lead) => lead.leadType)
          .filter((leadType): leadType is string => !!leadType),
      );
      this.filterService.leadTypeOptions.set(
        Array.from(leadTypes).sort((a: string, b: string) => a.localeCompare(b)),
      );
    }
  }

  adaptiveTableLayout(mediaQueryService: MediaQueryService) {
    return mediaQueryService.addListener(
      '(max-width: 700px)',
      (smallWidth: boolean) => {
        if (smallWidth) {
          // on very small devices, combine the postalCode and city into one column
          this.columnsToDisplay = [
            'name',
            'postalCodeCity',
            'priority',
            'select',
          ];
        } else {
          this.columnsToDisplay = [
            'name',
            'postalCode',
            'city',
            'priority',
            'select',
          ];
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.mediaQueryList.unsubscribe();
    this.leadServiceSubscription?.unsubscribe();
  }

  protected readonly input = input;
}
